export interface ChangelogVersion {
  version: string;
  filename: string;
}

/**
 * Fetches the list of available changelog versions
 * by reading the manifest.json file in the changelogs directory
 */
export async function getChangelogVersions(): Promise<ChangelogVersion[]> {
  try {
    // Fetch the manifest file that lists all available changelogs
    const manifestResponse = await fetch('/changelogs/manifest.json');
    if (manifestResponse.ok) {
      const manifest = await manifestResponse.json();
      const versions = manifest.versions || [];
      
      // Sort by version number (newest first)
      return versions.sort((a: ChangelogVersion, b: ChangelogVersion) => {
        const aParts = a.version.split('.').map(Number);
        const bParts = b.version.split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aPart = aParts[i] || 0;
          const bPart = bParts[i] || 0;
          if (bPart !== aPart) {
            return bPart - aPart;
          }
        }
        return 0;
      });
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching changelog versions:', error);
    return [];
  }
}

/**
 * Checks if a changelog file exists
 */
async function checkChangelogExists(filename: string): Promise<boolean> {
  try {
    const response = await fetch(`/changelogs/${filename}`, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Fetches the content of a specific changelog file
 */
export async function getChangelogContent(filename: string): Promise<string | null> {
  try {
    const response = await fetch(`/changelogs/${filename}`);
    if (response.ok) {
      return await response.text();
    }
    return null;
  } catch (error) {
    console.error(`Error fetching changelog ${filename}:`, error);
    return null;
  }
}
